import { useMemo } from 'react'
import {
  ComposedChart,
  Bar,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from 'recharts'
import { format } from 'date-fns'
import type { OHLCVCandle, PredictionResult } from '../../types'
import { formatPriceWithSymbol } from '../../types'
import { useStore } from '../../store/useStore'

interface CandleBarProps {
  x?: number
  y?: number
  width?: number
  height?: number
  payload?: ChartDataPoint
}

interface ChartDataPoint {
  timestamp: number
  open: number
  high: number
  low: number
  close: number
  volume: number
  isPrediction: boolean
  // For the body bar: [low, high] range so recharts places it; we draw custom
  bodyRange: [number, number]
  bodyColor: string
}

// Custom candlestick bar shape
function CandleShape(props: CandleBarProps) {
  const { x = 0, y = 0, width = 0, payload } = props
  if (!payload) return null

  const { open, close, isPrediction } = payload
  const isUp = close >= open

  // Colors
  const bodyColor = isPrediction
    ? isUp
      ? 'rgba(0,212,170,0.45)'
      : 'rgba(255,77,109,0.45)'
    : isUp
    ? '#00d4aa'
    : '#ff4d6d'

  const wickColor = isPrediction ? 'rgba(148,163,184,0.5)' : '#64748b'

  // We need the chart's yAxis scale. We use the y and height from recharts which map [low, high] bar range.
  // But recharts gives y/height for the full bar range (bodyRange).
  // We'll repurpose: recharts gives us the pixel position for our [min(open,close), max(open,close)] range.
  const { height: barHeight = 0 } = props

  if (barHeight === 0) return null

  const totalRange = payload.high - payload.low
  if (totalRange === 0) return null

  const bodyLow = Math.min(open, close)
  const bodyHigh = Math.max(open, close)

  // Fraction of body within the full high-low range
  const bodyTop_frac = (payload.high - bodyHigh) / totalRange
  const bodyBot_frac = (payload.high - bodyLow) / totalRange

  // Pixel positions (y is top of the high-low range, y+barHeight is bottom)
  const pxTop = y + bodyTop_frac * barHeight
  const pxBot = y + bodyBot_frac * barHeight
  const bodyH = Math.max(1, pxBot - pxTop)

  const cx = x + width / 2
  const wickX = cx

  return (
    <g>
      {/* Wick: high to low */}
      <line x1={wickX} y1={y} x2={wickX} y2={y + barHeight} stroke={wickColor} strokeWidth={1} />
      {/* Body */}
      <rect
        x={x + width * 0.15}
        y={pxTop}
        width={width * 0.7}
        height={bodyH}
        fill={bodyColor}
        stroke={isUp ? '#00d4aa' : '#ff4d6d'}
        strokeWidth={isPrediction ? 0.5 : 1}
        opacity={isPrediction ? 0.7 : 1}
      />
    </g>
  )
}

interface Props {
  ohlcv: OHLCVCandle[]
  prediction?: PredictionResult | null
  height?: number
  interval?: string
}

export default function CandlestickChart({ ohlcv, prediction, height = 420, interval }: Props) {
  const { selectedSymbol } = useStore()
  const { chartData, priceMin, priceMax } = useMemo(() => {
    const historical = ohlcv.slice(-100).map((c): ChartDataPoint => ({
      timestamp: c.timestamp,
      open: c.open,
      high: c.high,
      low: c.low,
      close: c.close,
      volume: c.volume,
      isPrediction: false,
      bodyRange: [c.low, c.high],
      bodyColor: c.close >= c.open ? '#00d4aa' : '#ff4d6d',
    }))

    // 1Dタブは24hの最終1点のみを1本の予測バーとして表示する
    const predTimestamps = prediction
      ? (interval === '1d' ? [prediction.timestamps[prediction.timestamps.length - 1]] : prediction.timestamps)
      : []
    const predIndexes = interval === '1d' && prediction
      ? [prediction.timestamps.length - 1]
      : prediction ? prediction.timestamps.map((_, i) => i) : []

    // ステップ0のIQRを基準にすることでMC累積分散による値幅膨張を防ぐ
    const baseHalf = prediction
      ? (prediction.p75[0] - prediction.p25[0]) *
        ((interval === '1h' || interval === '1d') ? 0.3 : 0.5)
      : 0

    const predPoints: ChartDataPoint[] = prediction
      ? predIndexes.map((srcIdx, i) => {
          const prevClose = i === 0 ? (ohlcv[ohlcv.length - 1]?.close ?? prediction.median[srcIdx]) : prediction.median[predIndexes[i - 1]]
          const close = prediction.median[srcIdx]
          // 1D: ドージ足（open=close=median）で方向感はウィックのみで表現
          // 1H/4H: 連続バー（前ステップ終値をopen）
          const open = interval === '1d' ? close : prevClose
          const high = Math.max(open, close, close + baseHalf)
          const low  = Math.min(open, close, close - baseHalf)
          const isUp = interval === '1d'
            ? close >= (ohlcv[ohlcv.length - 1]?.close ?? close)
            : close >= prevClose
          return {
            timestamp: predTimestamps[i],
            open,
            high,
            low,
            close,
            volume: 0,
            isPrediction: true,
            bodyRange: [Math.min(open, close), Math.max(open, close)],
            bodyColor: isUp ? 'rgba(0,212,170,0.4)' : 'rgba(255,77,109,0.4)',
          }
        })
      : []

    const combined = [...historical, ...predPoints]

    // Price domain
    const allPrices = combined.flatMap((c) => [c.low, c.high])
    const pMin = Math.min(...allPrices)
    const pMax = Math.max(...allPrices)
    const pad = (pMax - pMin) * 0.05

    return { chartData: combined, priceMin: pMin - pad, priceMax: pMax + pad }
  }, [ohlcv, prediction])

  // Separation timestamp (where prediction begins)
  const separationTs = prediction && ohlcv.length > 0 ? ohlcv[ohlcv.length - 1].timestamp : null

  // Area data for uncertainty bands (only prediction points)
  const bandData = useMemo(() => {
    if (!prediction) return []
    return prediction.timestamps.map((ts, i) => ({
      timestamp: ts,
      p10_p90: [prediction.p10[i], prediction.p90[i]] as [number, number],
      p25_p75: [prediction.p25[i], prediction.p75[i]] as [number, number],
      median: prediction.median[i],
    }))
  }, [prediction])

  // Merge band data into chart data for area rendering
  const mergedData = useMemo(() => {
    const bandMap = new Map(bandData.map((d) => [d.timestamp, d]))
    return chartData.map((d) => {
      const band = bandMap.get(d.timestamp)
      return {
        ...d,
        p10_p90: band?.p10_p90,
        p25_p75: band?.p25_p75,
        medianLine: band?.median,
      }
    })
  }, [chartData, bandData])

  function formatXAxis(ts: number) {
    return format(new Date(ts), 'MM/dd HH:mm')
  }

  function formatPrice(v: number) {
    return formatPriceWithSymbol(v, selectedSymbol)
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null
    const d = payload[0]?.payload as ChartDataPoint
    if (!d) return null
    return (
      <div className="bg-trading-panel border border-trading-border rounded-lg p-3 text-xs font-mono shadow-xl">
        <div className="text-trading-muted mb-2">{format(new Date(d.timestamp), 'MMM dd, yyyy HH:mm')}</div>
        {d.isPrediction ? (
          <div className="text-trading-yellow font-semibold mb-1">Prediction</div>
        ) : null}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <span className="text-trading-muted">O</span>
          <span className="text-trading-text tabular-nums">{formatPrice(d.open)}</span>
          <span className="text-trading-muted">H</span>
          <span className="text-trading-green tabular-nums">{formatPrice(d.high)}</span>
          <span className="text-trading-muted">L</span>
          <span className="text-trading-red tabular-nums">{formatPrice(d.low)}</span>
          <span className="text-trading-muted">C</span>
          <span className={d.close >= d.open ? 'text-trading-green tabular-nums' : 'text-trading-red tabular-nums'}>
            {formatPrice(d.close)}
          </span>
          {!d.isPrediction && (
            <>
              <span className="text-trading-muted">V</span>
              <span className="text-trading-text tabular-nums">
                {d.volume > 1e6
                  ? `${(d.volume / 1e6).toFixed(2)}M`
                  : d.volume > 1e3
                  ? `${(d.volume / 1e3).toFixed(1)}K`
                  : d.volume.toFixed(0)}
              </span>
            </>
          )}
        </div>
      </div>
    )
  }

  if (!ohlcv.length) {
    return (
      <div className="chart-container flex items-center justify-center" style={{ height }}>
        <p className="text-trading-muted text-sm">No data available</p>
      </div>
    )
  }

  // Volume chart data (only historical)
  const maxVol = Math.max(...ohlcv.slice(-100).map((c) => c.volume), 1)

  return (
    <div className="chart-container" style={{ height }}>
      {/* Price chart — 80% height */}
      <div style={{ height: `${Math.floor(height * 0.78)}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={mergedData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
            <XAxis
              dataKey="timestamp"
              tickFormatter={formatXAxis}
              tick={{ fontSize: 10, fill: '#64748b', fontFamily: 'monospace' }}
              tickLine={false}
              axisLine={{ stroke: '#1f2937' }}
              interval="preserveStartEnd"
              minTickGap={60}
            />
            <YAxis
              domain={[priceMin, priceMax]}
              tickFormatter={formatPrice}
              tick={{ fontSize: 10, fill: '#64748b', fontFamily: 'monospace' }}
              tickLine={false}
              axisLine={false}
              width={68}
              orientation="right"
            />
            <Tooltip content={<CustomTooltip />} />

            {/* P10-P90 uncertainty band */}
            <Area
              dataKey="p10_p90"
              fill="rgba(59,130,246,0.08)"
              stroke="none"
              connectNulls={false}
              isAnimationActive={false}
            />

            {/* P25-P75 band */}
            <Area
              dataKey="p25_p75"
              fill="rgba(59,130,246,0.14)"
              stroke="none"
              connectNulls={false}
              isAnimationActive={false}
            />

            {/* Candlestick bars */}
            <Bar
              dataKey="bodyRange"
              shape={<CandleShape />}
              isAnimationActive={false}
              maxBarSize={16}
            />

            {/* Predicted median line */}
            {prediction && (
              <Line
                dataKey="medianLine"
                stroke="#f97316"
                strokeWidth={2}
                strokeDasharray="5 3"
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
            )}

            {/* Vertical separator between history and prediction */}
            {separationTs && (
              <ReferenceLine
                x={separationTs}
                stroke="#3b82f6"
                strokeDasharray="4 4"
                strokeOpacity={0.5}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Volume chart — 20% height */}
      <div style={{ height: `${Math.floor(height * 0.18)}px` }} className="mt-1">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={mergedData.filter((d) => !d.isPrediction)}
            margin={{ top: 0, right: 12, left: 0, bottom: 0 }}
          >
            <YAxis hide domain={[0, maxVol * 1.2]} />
            <XAxis dataKey="timestamp" hide />
            <Bar
              dataKey="volume"
              maxBarSize={12}
              isAnimationActive={false}
              fill="#1e3a5f"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
